import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { CompanyDeleteCredentialModel } from 'src/app/models/authentication/company-delete-credential.model';
import { ItemFilterModel } from 'src/app/models/filters/item-filter.model';
import { CompanyModel } from 'src/app/models/management/company.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {

  constructor(private _httpClient: HttpClient) { }

  createCompany(companyModel: CompanyModel): Observable<CompanyModel> {
    return this._httpClient.post<CompanyModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.CreateCompany}`,
      companyModel);
  }

  deleteCompany(companyId: number): Observable<CompanyModel> {
    return this._httpClient.delete<CompanyModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.DeleteCompany}/${companyId}`);
  }

  getAllAccountingSystemsForSelect(): Observable<Array<CustomListDropdown>> {
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetAllAccountingSystemsForSelectAsync}`);
  }

  getAllArticlesByCompanyIdForSelect(companyId: number): Observable<Array<CustomListDropdown>> {
    const params = new HttpParams().set('companyId', companyId);
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetAllArticlesByCompanyIdForSelectAsync}`,
      { params: params }
    );
  }

  getAllCompaniesForSelectAsync(): Observable<Array<CustomListDropdown>> {
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetAllCompaniesForSelectAsync}`);
  }

  getAllCompaniesWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetAllCompaniesWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getAllConceptInvoicingsForSelect(companyId: number): Observable<Array<CustomListDropdown>> {
    const params = new HttpParams().set('companyId', companyId);
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetAllConceptInvoicingsForSelectAsync}`,
      { params: params }
    );
  }

  getAllCountriesForSelect(): Observable<Array<CustomListDropdown>> {
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetAllCountriesForSelectAsync}`);
  }

  getAllCurrenciesForSelect(): Observable<Array<CustomListDropdown>> {
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetAllCurrenciesForSelectAsync}`);
  }

  getAllTypeAccountingSystemsForSelect(): Observable<Array<CustomListDropdown>> {
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetAllTypeAccountingSystemsForSelectAsync}`);
  }

  getAllTypeDocumentsForSelect(): Observable<Array<CustomListDropdown>> {
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetAllTypeDocumentsForSelectAsync}`);
  }

  getCompanyById(companyId: number | null): Observable<CompanyModel> {
    return this._httpClient.get<CompanyModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetByIdAsync}/${companyId}`)
  }

  getCompanyByCode(code: number): Observable<CompanyModel> {
    return this._httpClient.get<CompanyModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetCompanyByCode}/${code}`)
  }

  getCompanyByUserAutheticated(): Observable<CompanyModel> {
    return this._httpClient.get<CompanyModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetCompanyByUserAutheticated}`);
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetColumnsForGeneralConsultationAsync}`);
  }

  getDigitVerification(document: string): Observable<number> {
    const itemFilterResponse = new ItemFilterModel("document", null, null, "");
    itemFilterResponse.wildcard = document;
    return this._httpClient.post<number>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetDigitVerification}`, itemFilterResponse);
  }

  getFirstCompanyByKeysAsync(): Observable<CompanyModel> {
    return this._httpClient.get<CompanyModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetFirstCompanyByKeysAsync}`);
  }

  getLastCompanyByKeysAsync(): Observable<CompanyModel> {
    return this._httpClient.get<CompanyModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetLastCompanyByKeysAsync}`);
  }

  getNextCompanyByKeysAsync(code: number): Observable<CompanyModel> {
    const params = new HttpParams().set('code', code);
    return this._httpClient.get<CompanyModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetNextCompanyByKeysAsync}`,
      { params: params }
    );
  }

  getPreviusCompanyByKeysAsync(code: number): Observable<CompanyModel> {
    const params = new HttpParams().set('code', code);
    return this._httpClient.get<CompanyModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.GetPreviusCompanyByKeysAsync}`,
      { params: params }
    );
  }

  setActiveCompany(companyId: number): Observable<CompanyModel> {
    return this._httpClient.put<CompanyModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.SetActiveCompany}/${companyId}`,
      null);
  }

  updateCompany(companyModel: CompanyModel): Observable<CompanyModel> {
    return this._httpClient.put<CompanyModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.UpdateCompany}`,
      companyModel);
  }

  validateDeleteCompany(model: CompanyDeleteCredentialModel): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.company.ValidateDeleteCompanyCredentials}`,
      model
    );
  }
}

